import React, { useEffect, useState } from 'react';
import api from '../utils/api';
import { RefreshCw, Play, RotateCcw, Search, Zap } from 'lucide-react';

const TaskQueue = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [statusFilter, setStatusFilter] = useState('all');
  const [formData, setFormData] = useState({ task_type: 'tally_sync', payload: {}, max_retries: 3 });
  const [processing, setProcessing] = useState(false);

  useEffect(() => { fetchData(); }, []);
  const fetchData = async () => {
    setLoading(true);
    try { const r = await api.get('/workflow/task-queue'); setTasks(r.data); }
    catch(e) { console.error(e); } finally { setLoading(false); }
  };

  const enqueueTask = async (e) => {
    e.preventDefault();
    try { await api.post('/workflow/task-queue', formData); setShowModal(false); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const processTask = async (id) => {
    try { await api.post(`/workflow/task-queue/${id}/process`); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const retryTask = async (id) => {
    try { await api.post(`/workflow/task-queue/${id}/retry`); fetchData(); }
    catch (err) { alert(err.response?.data?.detail || 'Failed'); }
  };

  const processAll = async () => {
    setProcessing(true);
    try {
      const r = await api.post('/workflow/task-queue/process-all');
      alert(`Processed: ${r.data.processed}, Failed: ${r.data.failed}, Mode: ${r.data.mode}`);
      fetchData();
    } catch (err) { alert(err.response?.data?.detail || 'Failed'); }
    finally { setProcessing(false); }
  };

  const getStatusBadge = (s) => {
    const m = { queued: 'badge-warning', processing: 'badge-info', completed: 'badge-success', failed: 'badge-danger' };
    return `badge ${m[s] || 'badge-info'}`;
  };

  const getResultDisplay = (t) => {
    if (t.error) return <span style={{ color: 'var(--danger)' }}>{t.error}</span>;
    if (t.result) {
      const r = t.result;
      if (r.transmitted === true) return <span style={{ color: 'var(--success)' }}>Transmitted to Tally</span>;
      if (r.mode === 'test') return <span style={{ color: 'var(--warning)' }}>Test mode (not transmitted)</span>;
      return <span>{r.message || JSON.stringify(r)}</span>;
    }
    return '-';
  };

  const filtered = tasks.filter(t => statusFilter === 'all' || t.status === statusFilter);

  return (
    <div data-testid="task-queue-page">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h1 style={{ fontSize: '24px', fontWeight: 700, margin: 0 }}>Async Task Queue</h1>
          <p style={{ margin: '4px 0 0', color: 'var(--text-tertiary)', fontSize: '13px' }}>MongoDB-based async queue for Tally sync and background tasks</p>
        </div>
        <div className="flex gap-2">
          <button onClick={fetchData} className="btn btn-secondary btn-sm" data-testid="refresh-btn"><RefreshCw size={14} /> Refresh</button>
          <button onClick={processAll} className="btn btn-warning btn-sm" disabled={processing} data-testid="process-all-btn"><Zap size={14} /> {processing ? 'Processing...' : 'Process All'}</button>
          <button onClick={() => setShowModal(true)} className="btn btn-primary btn-sm" data-testid="add-task-btn"><Play size={14} /> Enqueue Task</button>
        </div>
      </div>

      <div className="grid grid-cols-4 mb-4">
        {['queued', 'processing', 'completed', 'failed'].map(st => (
          <div key={st} className="stat-card" data-testid={`stat-${st}`}>
            <div className="stat-value">{tasks.filter(t => t.status === st).length}</div>
            <div className="stat-label">{st}</div>
          </div>
        ))}
      </div>

      <div className="card mb-4">
        <div className="flex gap-4">
          <select className="form-select" style={{ width: '160px' }} value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} data-testid="status-filter">
            <option value="all">All Status</option>
            <option value="queued">Queued</option><option value="processing">Processing</option><option value="completed">Completed</option><option value="failed">Failed</option>
          </select>
        </div>
      </div>

      <div className="card">
        {loading ? <div>Loading...</div> : (
          <div className="table-container">
            <table>
              <thead><tr><th>Task ID</th><th>Type</th><th>Status</th><th>Retries</th><th>Created</th><th>Processed</th><th>Result/Error</th><th>Actions</th></tr></thead>
              <tbody>
                {filtered.length === 0 ? <tr><td colSpan={8} style={{ textAlign: 'center', padding: '20px', color: 'var(--text-tertiary)' }}>No tasks</td></tr> : filtered.map(t => (
                  <tr key={t.id} data-testid={`task-row-${t.id}`}>
                    <td className="font-mono text-xs">{t.id.substring(0, 8)}...</td>
                    <td><span className="badge badge-info">{t.task_type}</span></td>
                    <td><span className={getStatusBadge(t.status)}>{t.status}</span></td>
                    <td className="tabular-nums">{t.retries}/{t.max_retries}</td>
                    <td className="text-sm">{t.created_at ? new Date(t.created_at).toLocaleString() : '-'}</td>
                    <td className="text-sm">{t.processed_at ? new Date(t.processed_at).toLocaleString() : '-'}</td>
                    <td className="text-xs" style={{ maxWidth: '250px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{getResultDisplay(t)}</td>
                    <td>
                      <div className="flex gap-2">
                        {t.status === 'queued' && <button onClick={() => processTask(t.id)} className="btn btn-ghost btn-sm" title="Process" data-testid={`process-${t.id}`}><Play size={14} color="#059669" /></button>}
                        {t.status === 'failed' && <button onClick={() => retryTask(t.id)} className="btn btn-ghost btn-sm" title="Retry" data-testid={`retry-${t.id}`}><RotateCcw size={14} color="#D97706" /></button>}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showModal && (
        <div className="modal-overlay" data-testid="task-modal">
          <div className="modal-content" style={{ maxWidth: '450px' }} onClick={(e) => e.stopPropagation()}>
            <div className="modal-header"><h2 style={{ margin: 0, fontSize: '18px', fontWeight: 700 }}>Enqueue Task</h2></div>
            <form onSubmit={enqueueTask}>
              <div className="modal-body">
                <div className="form-group">
                  <label className="form-label">Task Type</label>
                  <select className="form-select" value={formData.task_type} onChange={(e) => setFormData({...formData, task_type: e.target.value})} data-testid="task-type-select">
                    <option value="tally_sync">Tally Sync</option>
                    <option value="tally_push_voucher">Push Voucher to Tally</option>
                    <option value="tally_pull_ledger">Pull Ledger from Tally</option>
                    <option value="export_report">Export Report</option>
                  </select>
                </div>
                <div className="form-group"><label className="form-label">Max Retries</label><input type="number" className="form-input" value={formData.max_retries} onChange={(e) => setFormData({...formData, max_retries: parseInt(e.target.value) || 3})} data-testid="max-retries" /></div>
              </div>
              <div className="modal-footer">
                <button type="button" onClick={() => setShowModal(false)} className="btn btn-secondary">Cancel</button>
                <button type="submit" className="btn btn-primary" data-testid="submit-task-btn">Enqueue</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default TaskQueue;
